import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
    Container,
    Card,
    Table,
    Button,
    Row,
    Col,
    Spinner,
    Form,
    InputGroup
} from "react-bootstrap";
import {
    FaBirthdayCake,
    FaBell,
    FaCheckCircle,
    FaSearch,
    FaFilter,
    FaCalendarAlt,
    FaClock,
    FaUserFriends,
    FaSave,
    FaUndo
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    updateDoc
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

const toSafeText = (value) => String(value ?? "").trim();

const parseBirthday = (raw) => {
    if (!raw) return null;

    if (raw?.seconds) {
        const dt = new Date(raw.seconds * 1000);
        if (Number.isNaN(dt.getTime())) return null;
        return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
    }

    if (raw instanceof Date) {
        if (Number.isNaN(raw.getTime())) return null;
        return new Date(raw.getUTCFullYear(), raw.getUTCMonth(), raw.getUTCDate());
    }

    const source = toSafeText(raw);
    if (!source) return null;

    const isoMatch = source.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
    if (isoMatch) {
        const [, y, m, d] = isoMatch;
        return new Date(Number(y), Number(m) - 1, Number(d));
    }

    const latinMatch = source.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (latinMatch) {
        const [, d, m, y] = latinMatch;
        return new Date(Number(y), Number(m) - 1, Number(d));
    }

    const parsed = new Date(source);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
};

const fmtDate = (date) => {
    if (!date || Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("es-PE");
};

const getNextBirthday = (birthDate, today) => {
    const currentYear = today.getFullYear();
    let next = new Date(currentYear, birthDate.getMonth(), birthDate.getDate());
    if (next < today) {
        next = new Date(currentYear + 1, birthDate.getMonth(), birthDate.getDate());
    }
    return next;
};

const MESES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

function Cumpleanos({ usuario }) {
    const [loading, setLoading] = useState(false);
    const [cumpleanos, setCumpleanos] = useState([]);
    const [filtro, setFiltro] = useState("mes");
    const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth());
    const [busqueda, setBusqueda] = useState("");
    const [resumen, setResumen] = useState({
        total: 0,
        hoy: 0,
        semana: 0,
        mes: 0,
    });

    // States for custom message template
    const [template, setTemplate] = useState("");
    const [originalTemplate, setOriginalTemplate] = useState("");
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [nombreEmpresa, setNombreEmpresa] = useState("Mi Boleta");

    const rolNormalizado = String(usuario?.rol || "").toLowerCase();
    const puedeEditar = rolNormalizado === "admin" || rolNormalizado === "superadmin";

    const ensureAuthenticated = () => {
        if (auth.currentUser) return true;
        Swal.fire("Sesión requerida", "Solo usuarios autenticados pueden usar Cumpleaños.", "warning");
        return false;
    };

    useEffect(() => {
        cargarCumpleanos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario]);

    const cargarCumpleanos = async () => {
        if (!ensureAuthenticated()) return;
        setLoading(true);
        try {
            const ruc = usuario?.ruc || usuario?.rucEmpresa;
            if (!ruc) {
                setCumpleanos([]);
                setResumen({ total: 0, hoy: 0, semana: 0, mes: 0 });
                return;
            }

            // Cargar datos de la empresa (nombre y plantilla de mensaje)
            const companyRef = doc(db, "company", ruc);
            const companySnap = await getDoc(companyRef);
            if (companySnap.exists()) {
                const data = companySnap.data();
                const nombreCom = data.nombreComercial || data.name || "Nuestra Empresa";
                setNombreEmpresa(nombreCom);
                const msg = data.mensajeCumpleanos || "";
                setTemplate(msg);
                setOriginalTemplate(msg);
            }

            const usersRef = collection(db, "users");
            const qUsers = query(
                usersRef,
                where("rucEmpresa", "==", ruc),
                where("activo", "==", true)
            );

            const [usersSnap, empleadosRucEmpresaSnap, empleadosRucSnap] = await Promise.all([
                getDocs(qUsers),
                getDocs(
                    query(
                        collection(db, "pl_empleados"),
                        where("ruc_empresa", "==", ruc),
                        where("activo", "==", true)
                    )
                ),
                getDocs(
                    query(
                        collection(db, "pl_empleados"),
                        where("ruc", "==", ruc),
                        where("activo", "==", true)
                    )
                )
            ]);

            const empleadosByDni = new Map();
            [...empleadosRucEmpresaSnap.docs, ...empleadosRucSnap.docs].forEach((docRef) => {
                const data = docRef.data() || {};
                const dni = toSafeText(data.dni || docRef.id);
                if (!dni) return;
                empleadosByDni.set(dni, data);
            });

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            let totalHoy = 0;
            let totalSemana = 0;
            let totalMes = 0;

            const listado = [];

            usersSnap.docs.forEach((docRef) => {
                const userData = docRef.data() || {};
                const dni = toSafeText(userData.dni || docRef.id);
                const ficha = empleadosByDni.get(dni) || {};

                const rawBirthday =
                    ficha.fecha_nacimiento ||
                    userData.fecha_nacimiento ||
                    "";

                const birthDate = parseBirthday(rawBirthday);
                if (!birthDate) return;

                const nextBirthday = getNextBirthday(birthDate, today);
                const diffTime = nextBirthday - today;
                const daysToBirthday = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                const birthMonth = birthDate.getMonth();
                const birthDay = birthDate.getDate();
                const esMesActual = birthMonth === today.getMonth();

                if (birthMonth === today.getMonth() && birthDay === today.getDate()) {
                    totalHoy += 1;
                }
                if (daysToBirthday >= 0 && daysToBirthday <= 7) {
                    totalSemana += 1;
                }
                if (esMesActual) {
                    totalMes += 1;
                }

                let estado = "Proximo";
                let color = "warning";
                if (birthMonth === today.getMonth() && birthDay === today.getDate()) {
                    estado = "Hoy";
                    color = "success";
                } else if (daysToBirthday > 0 && daysToBirthday <= 7) {
                    estado = "Esta semana";
                    color = "danger";
                } else if (esMesActual && birthDay < today.getDate()) {
                    estado = "Ya cumplió";
                    color = "secondary";
                }

                listado.push({
                    id: `${dni}-${nextBirthday.toISOString().slice(0, 10)}`,
                    dni,
                    empleado:
                        `${toSafeText(userData.name)} ${toSafeText(userData.apellidos)}`.trim() ||
                        toSafeText(userData.nombre) ||
                        "Empleado Desconocido",
                    area: toSafeText(userData.cecoNombre) || "General",
                    proximoCumple: nextBirthday,
                    diasRestantes: daysToBirthday,
                    estado,
                    color,
                    mesNacimiento: birthMonth,
                    diaNacimiento: birthDay
                });
            });

            setCumpleanos(listado);
            setResumen({
                total: listado.length,
                hoy: totalHoy,
                semana: totalSemana,
                mes: totalMes,
            });
        } catch (error) {
            console.error("Error al cargar cumpleanos:", error);
            Swal.fire("Error", "No se pudieron cargar los cumpleaños", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveTemplate = async () => {
        if (!puedeEditar) return;
        const ruc = usuario?.ruc || usuario?.rucEmpresa;
        if (!ruc) {
            Swal.fire("Error", "No se pudo obtener el RUC de la empresa.", "error");
            return;
        }

        setSavingTemplate(true);
        try {
            const companyRef = doc(db, "company", ruc);
            await updateDoc(companyRef, {
                mensajeCumpleanos: template.trim()
            });
            setOriginalTemplate(template.trim());
            Swal.fire({
                title: "¡Guardado!",
                text: "La plantilla del mensaje de cumpleaños se actualizó correctamente.",
                icon: "success",
                confirmButtonColor: "#3b82f6"
            });
        } catch (error) {
            console.error("Error al guardar plantilla:", error);
            Swal.fire("Error", "No se pudo guardar la plantilla del mensaje.", "error");
        } finally {
            setSavingTemplate(false);
        }
    };

    const handleRestoreDefault = () => {
        const defaultMsg = "🎉 Estimado/a {nombre}, {empresa} te desea un feliz cumpleaños y muchos éxitos en este nuevo año.";
        setTemplate(defaultMsg);
    };

    const insertPlaceholder = (tag) => {
        setTemplate((prev) => prev + tag);
    };

    const getPreviewMessage = () => {
        const text = template || "🎉 Estimado/a {nombre}, {empresa} te desea un feliz cumpleaños y muchos éxitos en este nuevo año.";
        return text
            .replace(/{nombre}/g, "Juan")
            .replace(/{primerNombre}/g, "Juan")
            .replace(/{nombreCompleto}/g, "Juan Pérez")
            .replace(/{empresa}/g, nombreEmpresa)
            .replace(/{nombreEmpresa}/g, nombreEmpresa);
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cumpleanosFiltrados = cumpleanos.filter((item) => {
        const tieneBusqueda = busqueda.trim().length > 0;
        const coincideBusqueda =
            item.empleado.toLowerCase().includes(busqueda.toLowerCase()) ||
            item.dni.includes(busqueda);

        const coincideFiltroEspecifico =
            (filtro === "hoy" && item.estado === "Hoy") ||
            (filtro === "semana" && item.diasRestantes >= 0 && item.diasRestantes <= 7) ||
            (filtro === "mes" && item.mesNacimiento === Number(mesSeleccionado));

        const coincideFiltro =
            filtro === "todos"
                ? (tieneBusqueda ? true : (item.diasRestantes >= 0 && item.diasRestantes <= 30))
                : coincideFiltroEspecifico;

        return coincideBusqueda && coincideFiltro;
    });

    if (filtro === "mes") {
        cumpleanosFiltrados.sort((a, b) => a.diaNacimiento - b.diaNacimiento);
    } else {
        cumpleanosFiltrados.sort((a, b) => a.diasRestantes - b.diasRestantes);
    }

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
                <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                            borderBottom: "3px solid #f59e0b"
                        }}
                    >
                        <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
                            <BotonManualHeader />
                        </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaBirthdayCake className="me-3" />
                                Cumpleaños
                            </h2>
                            <p className="mb-0 opacity-75">
                                Seguimiento de fechas de nacimiento de trabajadores y plantilla de felicitación
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)", color: "white", borderRadius: "15px" }}>
                                        <FaBirthdayCake size={28} className="mb-2 opacity-75" />
                                        <h4 className="fw-bold mb-0">{resumen.hoy}</h4>
                                        <p className="mb-0 small opacity-90">Cumplen Hoy</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                                        <FaClock size={28} className="mb-2 opacity-75" />
                                        <h4 className="fw-bold mb-0">{resumen.semana}</h4>
                                        <p className="mb-0 small opacity-90">Esta Semana</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)", color: "white", borderRadius: "15px" }}>
                                        <FaCalendarAlt size={28} className="mb-2 opacity-75" />
                                        <h4 className="fw-bold mb-0">{resumen.mes}</h4>
                                        <p className="mb-0 small opacity-90">Este Mes</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white", borderRadius: "15px" }}>
                                        <FaUserFriends size={28} className="mb-2 opacity-75" />
                                        <h4 className="fw-bold mb-0">{resumen.total}</h4>
                                        <p className="mb-0 small opacity-90">Con Fecha Registrada</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row>
                            <Col lg={8} md={12} className="mb-4">
                                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                                    <Card.Body className="p-3">
                                        <Row className="align-items-center g-2">
                                            <Col md={filtro === "mes" ? 4 : 5}>
                                                <InputGroup>
                                                    <InputGroup.Text className="bg-white border-end-0" style={{ borderRadius: "10px 0 0 10px" }}>
                                                        <FaSearch className="text-muted" />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        className="border-start-0"
                                                        placeholder="Buscar por nombre o DNI..."
                                                        value={busqueda}
                                                        onChange={(e) => setBusqueda(e.target.value)}
                                                        style={{ borderRadius: "0 10px 10px 0" }}
                                                    />
                                                </InputGroup>
                                            </Col>
                                            <Col md={filtro === "mes" ? 3 : 4}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaFilter className="text-muted flex-shrink-0" />
                                                    <Form.Select
                                                        value={filtro}
                                                        onChange={(e) => setFiltro(e.target.value)}
                                                        style={{ borderRadius: "10px" }}
                                                    >
                                                        <option value="mes">Cumpleaños del Mes</option>
                                                        <option value="hoy">Cumplen hoy</option>
                                                        <option value="semana">Próximos 7 días</option>
                                                        <option value="todos">Todos (Próximos 30 días)</option>
                                                    </Form.Select>
                                                </div>
                                            </Col>
                                            {filtro === "mes" && (
                                                <Col md={3}>
                                                    <Form.Select
                                                        value={mesSeleccionado}
                                                        onChange={(e) => setMesSeleccionado(Number(e.target.value))}
                                                        style={{ borderRadius: "10px" }}
                                                    >
                                                        {MESES.map((mes, idx) => (
                                                            <option key={idx} value={idx}>{mes}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                            )}
                                            <Col className="d-flex justify-content-end">
                                                <Button
                                                    onClick={cargarCumpleanos}
                                                    disabled={loading}
                                                    className="d-flex align-items-center gap-2 py-2 px-4"
                                                    style={{
                                                        borderRadius: "10px",
                                                        background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                        border: "none",
                                                        boxShadow: "0 4px 8px rgba(59, 130, 246, 0.3)"
                                                    }}
                                                >
                                                    {loading ? <Spinner animation="border" size="sm" /> : null}
                                                    Actualizar
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <Card className="shadow-sm border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
                                    <Card.Body className="p-0">
                                        <Table responsive hover className="mb-0 align-middle">
                                            <thead>
                                                <tr style={{
                                                    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                    color: "white",
                                                    fontSize: "0.82rem",
                                                    fontWeight: "600",
                                                    letterSpacing: "0.05em"
                                                }}>
                                                    <th className="py-3 px-3 border-0">EMPLEADO</th>
                                                    <th className="py-3 px-3 border-0">PROX. CUMPLE</th>
                                                    <th className="py-3 px-3 border-0 text-center">ESTADO</th>
                                                </tr>
                                            </thead>
                                            <tbody style={{ fontSize: "0.9rem" }}>
                                                {cumpleanosFiltrados.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className="text-center py-5 text-muted">
                                                            <FaCheckCircle size={40} className="text-success mb-3 d-block mx-auto" />
                                                            No se encontraron registros con los filtros actuales
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    cumpleanosFiltrados.map((item) => (
                                                        <tr key={item.id}>
                                                            <td className="py-3 px-3">
                                                                <div className="fw-bold">{item.empleado}</div>
                                                                <small className="text-muted">{item.dni} | {item.area}</small>
                                                            </td>
                                                            <td className="py-3 px-3">
                                                                <div className="fw-bold">
                                                                    {item.estado === "Ya cumplió"
                                                                        ? fmtDate(new Date(today.getFullYear(), item.mesNacimiento, item.diaNacimiento))
                                                                        : fmtDate(item.proximoCumple)}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {item.estado === "Hoy"
                                                                        ? "Cumple hoy 🎉"
                                                                        : item.estado === "Ya cumplió"
                                                                            ? "Ya cumplió este mes"
                                                                            : `Faltan ${item.diasRestantes} días`}
                                                                </small>
                                                            </td>
                                                            <td className="py-3 px-3 text-center">
                                                                <span
                                                                    className="px-3 py-1 rounded-pill text-white small fw-semibold"
                                                                    style={{
                                                                        background: item.color === "danger"
                                                                            ? "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)"
                                                                            : item.color === "warning"
                                                                                ? "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)"
                                                                                : item.color === "success"
                                                                                    ? "linear-gradient(135deg, #059669 0%, #10b981 100%)"
                                                                                    : "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)",
                                                                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                                                                    }}
                                                                >
                                                                    {item.estado}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={4} md={12}>
                                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                                    <Card.Header className="bg-white border-0 pt-4 pb-0">
                                        <h5 className="fw-bold mb-0 text-primary d-flex align-items-center gap-2">
                                            <FaBell />
                                            Mensaje de Cumpleaños
                                        </h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="text-muted small">
                                            Personaliza el mensaje automático de felicitación de cumpleaños que se envía a tus trabajadores.
                                        </p>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="small fw-semibold">Mensaje de Felicitación</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                placeholder="Escribe el mensaje aquí..."
                                                value={template}
                                                onChange={(e) => setTemplate(e.target.value)}
                                                disabled={!puedeEditar}
                                                style={{ borderRadius: "10px", fontSize: "0.9rem" }}
                                            />
                                        </Form.Group>

                                        <div className="mb-4">
                                            <div className="text-muted small mb-2 fw-semibold">Etiquetas dinámicas (haz clic para insertar):</div>
                                            <div className="d-flex flex-wrap gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => insertPlaceholder("{nombre}")}
                                                    disabled={!puedeEditar}
                                                    style={{ borderRadius: "20px", fontSize: "0.75rem" }}
                                                >
                                                    + {'{nombre}'}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => insertPlaceholder("{nombreCompleto}")}
                                                    disabled={!puedeEditar}
                                                    style={{ borderRadius: "20px", fontSize: "0.75rem" }}
                                                >
                                                    + {'{nombreCompleto}'}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => insertPlaceholder("{empresa}")}
                                                    disabled={!puedeEditar}
                                                    style={{ borderRadius: "20px", fontSize: "0.75rem" }}
                                                >
                                                    + {'{empresa}'}
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <div className="text-muted small mb-2 fw-semibold">Emojis de cumpleaños (haz clic para insertar):</div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {["🎂", "🎉", "🥳", "🎈", "🎁", "✨", "🎊", "🌟"].map((emoji) => (
                                                    <Button
                                                        key={emoji}
                                                        size="sm"
                                                        variant="light"
                                                        onClick={() => insertPlaceholder(emoji)}
                                                        disabled={!puedeEditar}
                                                        style={{ borderRadius: "8px", padding: "4px 10px", fontSize: "1.1rem", border: "1px solid #dee2e6" }}
                                                    >
                                                        {emoji}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>

                                        <Card className="border-0 mb-4" style={{ backgroundColor: "#f1f5f9", borderRadius: "15px" }}>
                                            <Card.Body className="p-3">
                                                <div className="small fw-bold text-muted mb-2 text-center" style={{ letterSpacing: "0.05em", fontSize: "0.75rem" }}>
                                                    VISTA PREVIA DE NOTIFICACIÓN
                                                </div>
                                                <div
                                                    className="p-3 position-relative shadow-sm"
                                                    style={{
                                                        backgroundColor: "#ffffff",
                                                        borderRadius: "12px",
                                                        fontSize: "0.85rem",
                                                        lineHeight: "1.4",
                                                        color: "#1f2937",
                                                        maxWidth: "100%",
                                                        wordBreak: "break-word",
                                                        borderLeft: "4px solid #3b82f6"
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center gap-2 mb-1 text-muted fw-semibold" style={{ fontSize: "0.75rem" }}>
                                                        <FaBell className="text-primary" size={12} />
                                                        <span>Notificación de Cumpleaños</span>
                                                    </div>
                                                    <div style={{ whiteSpace: "pre-wrap" }}>
                                                        {getPreviewMessage()}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        {puedeEditar ? (
                                            <div className="d-flex gap-2">
                                                <Button
                                                    onClick={handleSaveTemplate}
                                                    disabled={savingTemplate || template === originalTemplate}
                                                    className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                                                    style={{
                                                        borderRadius: "10px",
                                                        background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                        border: "none"
                                                    }}
                                                >
                                                    {savingTemplate ? <Spinner animation="border" size="sm" /> : <FaSave />}
                                                    Guardar Cambios
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    onClick={handleRestoreDefault}
                                                    title="Restaurar por defecto"
                                                    style={{ borderRadius: "10px" }}
                                                >
                                                    <FaUndo />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="text-center text-danger small bg-danger bg-opacity-10 py-2 px-3 rounded" style={{ borderRadius: "10px" }}>
                                                Solo los administradores pueden editar este mensaje.
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default Cumpleanos;
